import { useRef, useState } from 'react';
import { CheckCircle2, FileText, Loader2, MapPin, Paperclip, X } from 'lucide-react';

import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { compressPhoto } from '@/lib/imageCapture';

/**
 * Shared building blocks for the public application forms.
 *
 * Extracted from Apply.tsx when the chain form (/apply/:token) arrived, so the
 * two pages share one address verifier, one upload widget and one set of
 * field chrome rather than drifting into two visually-similar forms with
 * different validation.
 */

/* ------------------------------ addresses ------------------------------ */

/** Standardized candidate returned by the address-lookup verifier. */
export interface VerifiedAddress {
  formatted: string;
  line1: string;
  city: string;
  state: string;
  zip: string;
}

/** One address as the applicant types it — separate fields, per Sky. */
export interface StructuredAddr {
  street: string;
  unit: string;
  city: string;
  state: string;
  zip: string;
  /** True once USPS-standardized (accepted a suggestion or exact match). */
  verified: boolean;
  /** Applicant chose "keep as entered" over the suggestion. */
  dismissed: boolean;
}

export const EMPTY_ADDR: StructuredAddr = {
  street: '', unit: '', city: '', state: '', zip: '', verified: false, dismissed: false,
};

export function addrEmpty(a: StructuredAddr): boolean {
  return !a.street.trim() && !a.unit.trim() && !a.city.trim() && !a.state.trim() && !a.zip.trim();
}

export function addrComplete(a: StructuredAddr): boolean {
  return !!(
    a.street.trim() &&
    a.city.trim() &&
    /^[A-Za-z]{2}$/.test(a.state.trim()) &&
    /^\d{5}(-\d{4})?$/.test(a.zip.trim())
  );
}

export function addrBlob(a: StructuredAddr): string {
  const unit = a.unit.trim() ? `, ${a.unit.trim()}` : '';
  return `${a.street.trim()}${unit}, ${a.city.trim()}, ${a.state.trim().toUpperCase()} ${a.zip.trim()}`;
}

export function addrJson(a: StructuredAddr) {
  return {
    line1: a.street.trim(),
    unit: a.unit.trim() || undefined,
    city: a.city.trim(),
    state: a.state.trim().toUpperCase(),
    zip: a.zip.trim(),
    formatted: addrBlob(a),
    verified: a.verified,
  };
}

/* -------------------------------- chrome -------------------------------- */

export function Section({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
}) {
  return (
    <fieldset className="space-y-4">
      <div>
        <legend className="font-display text-base font-semibold text-brix-ink">{title}</legend>
        {subtitle && <p className="text-xs text-brix-muted mt-0.5">{subtitle}</p>}
      </div>
      {children}
    </fieldset>
  );
}

export function Field({
  label,
  required,
  hint,
  children,
}: {
  label: string;
  required?: boolean;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      <Label>
        {label}
        {required && <span className="text-brix-warning ml-0.5">*</span>}
      </Label>
      {children}
      {hint && <p className="text-[11px] text-brix-muted">{hint}</p>}
    </div>
  );
}

export function Textarea({
  value,
  onChange,
  rows,
  placeholder,
}: {
  value: string;
  onChange: (value: string) => void;
  rows: number;
  placeholder?: string;
}) {
  return (
    <textarea
      value={value}
      onChange={(e) => onChange(e.target.value)}
      rows={rows}
      placeholder={placeholder}
      className="w-full rounded-lg border border-brix-border bg-brix-surface/80 backdrop-blur-sm px-3 py-2 text-sm text-brix-ink placeholder:text-brix-muted focus:outline-none focus:ring-2 focus:ring-brix-primary/40"
    />
  );
}

/* --------------------------- address field ------------------------------ */

/**
 * Structured address block: separate street / unit / city / state / ZIP
 * fields. When the ZIP is completed, the address auto-checks against the
 * address-lookup verifier (US Census geocoder — free, keyless): an exact
 * match verifies silently; a differing standardized form is offered as
 * "use suggested / keep as entered". Verification never blocks submission.
 */
export function StructuredAddressField({
  label,
  required,
  hint,
  addr,
  onChange,
  onRemove,
}: {
  label: string;
  required?: boolean;
  hint?: string;
  addr: StructuredAddr;
  onChange: (addr: StructuredAddr) => void;
  onRemove?: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [suggestion, setSuggestion] = useState<VerifiedAddress | null>(null);
  const [noMatch, setNoMatch] = useState(false);

  function set(key: keyof StructuredAddr, value: string) {
    const next = { ...addr, [key]: value, verified: false, dismissed: false };
    onChange(next);
    setSuggestion(null);
    setNoMatch(false);
    // Auto-check the moment the ZIP is complete.
    if (key === 'zip' && /^\d{5}$/.test(value.trim())) void check(next);
  }

  async function check(a: StructuredAddr) {
    if (!a.street.trim() || !/^\d{5}/.test(a.zip.trim()) || a.verified || a.dismissed) return;
    setBusy(true);
    setSuggestion(null);
    setNoMatch(false);
    try {
      const q = `${a.street.trim()}, ${a.city.trim()}, ${a.state.trim()} ${a.zip.trim()}`;
      const res = await fetch(`/.netlify/functions/address-lookup?q=${encodeURIComponent(q)}`);
      const data = (await res.json().catch(() => ({}))) as { candidates?: VerifiedAddress[] };
      const c = data.candidates?.[0] ?? null;
      if (!c) {
        setNoMatch(true);
        return;
      }
      const same =
        c.line1.toLowerCase() === a.street.trim().toLowerCase() &&
        c.city.toLowerCase() === a.city.trim().toLowerCase() &&
        c.state.toLowerCase() === a.state.trim().toLowerCase() &&
        c.zip === a.zip.trim();
      if (same) {
        onChange({ ...a, verified: true, dismissed: false });
      } else {
        setSuggestion(c);
      }
    } catch {
      setNoMatch(true);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-1.5 rounded-lg border border-brix-border px-3 py-3">
      <div className="flex items-center justify-between">
        <Label>
          {label}
          {required && <span className="text-brix-warning ml-0.5">*</span>}
        </Label>
        <div className="flex items-center gap-2">
          {busy && <Loader2 size={12} className="animate-spin text-brix-muted" />}
          {addr.verified && (
            <span className="inline-flex items-center gap-1 text-[11px] text-emerald-700">
              <CheckCircle2 size={11} /> Verified
            </span>
          )}
          {onRemove && (
            <button
              type="button"
              onClick={onRemove}
              aria-label={`Remove ${label}`}
              className="text-brix-muted hover:text-brix-ink cursor-pointer"
            >
              <X size={14} />
            </button>
          )}
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="sm:col-span-2">
          <Input
            value={addr.street}
            onChange={(e) => set('street', e.target.value)}
            placeholder="Street address"
            autoComplete="address-line1"
          />
        </div>
        <Input
          value={addr.unit}
          onChange={(e) => set('unit', e.target.value)}
          placeholder="Unit / suite (optional)"
          autoComplete="address-line2"
        />
      </div>
      <div className="grid grid-cols-4 gap-3">
        <div className="col-span-2">
          <Input
            value={addr.city}
            onChange={(e) => set('city', e.target.value)}
            placeholder="City"
            autoComplete="address-level2"
          />
        </div>
        <Input
          value={addr.state}
          onChange={(e) => set('state', e.target.value.toUpperCase().slice(0, 2))}
          placeholder="ST"
          maxLength={2}
          autoComplete="address-level1"
        />
        <Input
          value={addr.zip}
          onChange={(e) => set('zip', e.target.value.replace(/[^0-9-]/g, '').slice(0, 10))}
          onBlur={() => void check(addr)}
          placeholder="ZIP"
          inputMode="numeric"
          autoComplete="postal-code"
        />
      </div>

      {suggestion && (
        <div className="rounded-lg border border-brix-primary/30 bg-brix-primary-tint/30 px-3 py-2.5 space-y-2">
          <p className="text-[12px] text-brix-ink inline-flex items-center gap-1.5">
            <MapPin size={12} className="text-brix-primary shrink-0" />
            Did you mean: <strong>{suggestion.formatted}</strong>
          </p>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => {
                onChange({
                  ...addr,
                  street: suggestion.line1,
                  city: suggestion.city,
                  state: suggestion.state,
                  zip: suggestion.zip,
                  verified: true,
                  dismissed: false,
                });
                setSuggestion(null);
              }}
              className="rounded-md bg-brix-primary text-white text-[12px] font-semibold px-2.5 py-1.5 cursor-pointer"
            >
              Use suggested address
            </button>
            <button
              type="button"
              onClick={() => {
                onChange({ ...addr, dismissed: true });
                setSuggestion(null);
              }}
              className="text-[12px] font-semibold text-brix-muted hover:text-brix-ink cursor-pointer"
            >
              Keep as entered
            </button>
          </div>
        </div>
      )}
      {noMatch && (
        <p className="text-[11px] text-brix-muted">
          We couldn't verify that address automatically — double-check it, or continue as typed.
        </p>
      )}
      {hint && !suggestion && <p className="text-[11px] text-brix-muted">{hint}</p>}
    </div>
  );
}

/* ------------------------------- uploads -------------------------------- */

const MAX_PDF_BYTES = 2_500_000;

export interface AttachedDoc {
  name: string;
  type: string;
  data: string; // base64, no data: prefix
}

/** Read any accepted file into an upload payload; images are downscaled. */
export async function fileToDoc(file: File): Promise<AttachedDoc> {
  if (file.type === 'application/pdf') {
    if (file.size > MAX_PDF_BYTES) {
      throw new Error('PDFs must be under 2.5 MB — please attach a smaller file.');
    }
    const buf = new Uint8Array(await file.arrayBuffer());
    let binary = '';
    const CHUNK = 0x8000;
    for (let i = 0; i < buf.length; i += CHUNK) {
      binary += String.fromCharCode(...buf.subarray(i, i + CHUNK));
    }
    return { name: file.name, type: 'application/pdf', data: btoa(binary) };
  }
  // Anything else goes through the image pipeline (downscale + JPEG re-encode).
  const photo = await compressPhoto(file);
  URL.revokeObjectURL(photo.previewUrl);
  return { name: file.name, type: photo.content_type, data: photo.data_base64 };
}

export function DocUpload({
  label,
  doc,
  onAttach,
  onError,
}: {
  label: string;
  doc: AttachedDoc | null;
  onAttach: (doc: AttachedDoc | null) => void;
  onError: (msg: string) => void;
}) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [busy, setBusy] = useState(false);

  async function onPick(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setBusy(true);
    try {
      onAttach(await fileToDoc(file));
    } catch (err) {
      onError(err instanceof Error ? err.message : 'Could not read that file — please try another.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      {doc ? (
        <div className="flex items-center gap-2 rounded-lg border border-brix-border bg-brix-surface px-3 py-2 text-sm text-brix-ink">
          <FileText size={14} className="text-brix-primary shrink-0" />
          <span className="truncate flex-1">{doc.name}</span>
          <button
            type="button"
            onClick={() => onAttach(null)}
            aria-label={`Remove ${label}`}
            className="text-brix-muted hover:text-brix-ink cursor-pointer"
          >
            <X size={14} />
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={busy}
          className="w-full flex items-center justify-center gap-2 rounded-lg border border-dashed border-brix-border bg-brix-surface/60 px-3 py-2.5 text-sm text-brix-muted hover:border-brix-primary/50 hover:text-brix-ink transition-colors cursor-pointer"
        >
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Paperclip size={14} />}
          {busy ? 'Preparing…' : 'Photo or PDF'}
        </button>
      )}
      <input
        ref={inputRef}
        type="file"
        accept="image/*,application/pdf"
        className="hidden"
        onChange={(e) => void onPick(e)}
      />
    </div>
  );
}

/* ------------------------------ page chrome ----------------------------- */

/** The gradient + grid backdrop both public application pages sit on. */
export function ApplyBackdrop() {
  return (
    <>
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            'radial-gradient(ellipse 55% 45% at 50% 30%, rgba(31,78,121,0.10), transparent 70%), radial-gradient(ellipse 35% 25% at 85% 90%, rgba(244,180,0,0.06), transparent 60%)',
        }}
      />
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-[0.04]"
        style={{
          backgroundImage:
            'linear-gradient(rgba(31,78,121,1) 1px, transparent 1px), linear-gradient(90deg, rgba(31,78,121,1) 1px, transparent 1px)',
          backgroundSize: '32px 32px',
        }}
      />
    </>
  );
}
